import { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { useWallet } from '../components/context/WagmiWalletContext';
import { useContracts } from '../components/context/ContractContext';
import logger from '../utils/logger';

// Types for Aerodrome V2 fee management
export interface PoolHealth {
    pool: string;
    token0: string;
    token1: string;
    token0Symbol: string;
    token1Symbol: string;
    isStable: boolean;
    reserve0: string;
    reserve1: string;
    lpBalance: string;
    lpTotalSupply: string;
    sharePercentage: number;
    estimatedValue0: string;
    estimatedValue1: string;
    unclaimedFees0: string;
    unclaimedFees1: string;
    autoCompoundEnabled: boolean;
    lastCompoundTime: number;
    lastUpdated: number;
}

export interface AerodromeFeeMetrics {
    unclaimedFeesETH: string;
    unclaimedFeesMEMER: string;
    totalValueETH: string;
    isCollectionViable: boolean;
    estimatedGasCost: string;
    profitAfterGas: string;
    lastUpdated: number;
}

export interface AerodromeAutoCompoundConfig {
    enabled: boolean;
    minFeeThresholdETH: string;
    gasMultiplier: string;
    minInterval: string;
    targetPool: string;
    lastCompoundTime: number;
}

interface UseAerodromeManagementReturn {
    // Data
    feeMetrics: AerodromeFeeMetrics | null;
    pools: PoolHealth[];
    autoCompoundConfig: AerodromeAutoCompoundConfig | null;

    // Loading states
    loading: boolean;
    error: string | null;

    // Actions
    refreshData: () => Promise<void>;
    collectAllFees: () => Promise<string | null>;
    collectPoolFees: (poolAddress: string) => Promise<string | null>;
    collectAndReinvest: (poolAddress: string, minToken0: string, minToken1: string) => Promise<string | null>;
    enableAutoCompound: (poolAddress: string, threshold: string, gasMultiplier: string, interval: string) => Promise<string | null>;
    disableAutoCompound: (poolAddress: string) => Promise<string | null>;
}

export const useAerodromeManagement = (contractAddress?: string): UseAerodromeManagementReturn => {
    const { provider, signer, chainId } = useWallet();
    const { deploymentDetails } = useContracts();

    // State
    const [feeMetrics, setFeeMetrics] = useState<AerodromeFeeMetrics | null>(null);
    const [pools, setPools] = useState<PoolHealth[]>([]);
    const [autoCompoundConfig, setAutoCompoundConfig] = useState<AerodromeAutoCompoundConfig | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [contract, setContract] = useState<ethers.Contract | null>(null);

    // Get the actual contract address
    const actualContractAddress = contractAddress || deploymentDetails?.liquidity?.aerodromeSetup?.addressForUI;

    // Load ABI based on chain ID
    const loadABI = useCallback(async (chainId: string): Promise<any[] | null> => {
        try {
            const abiPath = `/abi/${chainId}/AerodromeLiquiditySetupABI.json`;
            const response = await fetch(abiPath);

            if (!response.ok) {
                logger.error(`Failed to load ABI from ${abiPath}`);
                return null;
            }

            const data = await response.json();

            // Handle different ABI formats
            if (Array.isArray(data)) {
                return data;
            } else if (data.abi && Array.isArray(data.abi)) {
                return data.abi;
            } else {
                logger.error('Invalid ABI format');
                return null;
            }
        } catch (error) {
            logger.error('Error loading ABI:', error);
            return null;
        }
    }, []);

    // Initialize contract
    useEffect(() => {
        const initContract = async () => {
            if (!actualContractAddress || !provider || !chainId) {
                setContract(null);
                return;
            }

            try {
                const abi = await loadABI(chainId.toString());
                if (!abi) {
                    setError('Failed to load contract ABI');
                    return;
                }

                const contractInstance = new ethers.Contract(
                    actualContractAddress,
                    abi,
                    signer || provider
                );

                setContract(contractInstance);
                setError(null);
                logger.log('Aerodrome contract initialized:', actualContractAddress);
            } catch (err) {
                logger.error('Error initializing contract:', err);
                setError('Failed to initialize contract');
            }
        };

        initContract();
    }, [actualContractAddress, provider, signer, chainId, loadABI]);

    // Helper function to convert Wei to ETH
    const weiToEth = (wei: ethers.BigNumber): string => {
        return ethers.utils.formatEther(wei);
    };

    // Helper function to format token amounts
    const formatToken = (amount: ethers.BigNumber): string => {
        return ethers.utils.formatEther(amount);
    };

    // Get pool address from deployment details
    const getPoolAddress = useCallback((): string | null => {
        return deploymentDetails?.liquidity?.aerodromePool?.addressForUI || null;
    }, [deploymentDetails]);

    // Refresh all data
    const refreshData = useCallback(async () => {
        if (!contract) {
            logger.warn('Contract not initialized');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            logger.log('Refreshing Aerodrome fee management data...');

            // Use the dedicated dashboard stats function first
            let dashboardStats = null;
            try {
                dashboardStats = await contract.getDashboardStats();
                logger.log('Dashboard stats:', {
                    totalPools: dashboardStats.totalPools.toString(),
                    totalValueETH: dashboardStats.totalValueETH.toString(),
                    totalUnclaimedFeesETH: dashboardStats.totalUnclaimedFeesETH.toString()
                });
            } catch (dashboardError) {
                logger.warn('getDashboardStats failed:', dashboardError);
            }

            // Get total unclaimed fees using the proper function
            let totalFee0 = ethers.BigNumber.from(0);
            let totalFee1 = ethers.BigNumber.from(0);
            let totalUnclaimedFeesETH = ethers.BigNumber.from(0);

            if (dashboardStats) {
                // Use dashboard stats if available
                totalUnclaimedFeesETH = dashboardStats.totalUnclaimedFeesETH;
                logger.log('Using dashboard stats for fees:', totalUnclaimedFeesETH.toString());
            } else {
                // Fallback to individual fee calls
                try {
                    const result = await contract.getTotalUnclaimedFees();
                    totalFee0 = result[0];
                    totalFee1 = result[1];
                    logger.log('Total unclaimed fees from getTotalUnclaimedFees:', {
                        totalFee0: totalFee0.toString(),
                        totalFee1: totalFee1.toString()
                    });

                    // For now, assume totalFee0 is the ETH value (this might need adjustment)
                    totalUnclaimedFeesETH = totalFee0;
                } catch (totalFeesError) {
                    logger.warn('getTotalUnclaimedFees failed:', totalFeesError);
                    // Keep all values as zero - this is correct for a newly created pool
                }
            }

            // Get gas price for calculations
            const gasPrice = await provider?.getGasPrice() || ethers.BigNumber.from('20000000000'); // 20 gwei fallback
            const estimatedGasCost = gasPrice.mul(150000); // ~150k gas for Aerodrome collect + reinvest

            // Check if collection is viable
            let isViable = false;
            if (totalUnclaimedFeesETH.gt(0)) {
                isViable = totalUnclaimedFeesETH.gt(estimatedGasCost.mul(3));
            }

            const profitAfterGas = totalUnclaimedFeesETH.sub(estimatedGasCost);

            // Log the actual values we're setting
            logger.log('Setting fee metrics:', {
                unclaimedFeesETH: weiToEth(totalFee0),
                unclaimedFeesMEMER: formatToken(totalFee1),
                totalValueETH: weiToEth(totalUnclaimedFeesETH),
                isCollectionViable: isViable
            });

            setFeeMetrics({
                unclaimedFeesETH: weiToEth(totalFee0),
                unclaimedFeesMEMER: formatToken(totalFee1),
                totalValueETH: weiToEth(totalUnclaimedFeesETH),
                isCollectionViable: isViable,
                estimatedGasCost: weiToEth(estimatedGasCost),
                profitAfterGas: weiToEth(profitAfterGas.gt(0) ? profitAfterGas : ethers.BigNumber.from(0)),
                lastUpdated: Date.now()
            });

            // Get pool address for later use
            const poolAddress = getPoolAddress();

            // Try to get pool health data using the better contract functions
            let poolsData: PoolHealth[] = [];
            try {
                // Use getAllPoolsHealth for comprehensive data
                const allPoolsHealth = await contract.getAllPoolsHealth();
                logger.log('getAllPoolsHealth result:', allPoolsHealth);

                if (allPoolsHealth && Array.isArray(allPoolsHealth) && allPoolsHealth.length > 0) {
                    logger.log(`Found ${allPoolsHealth.length} pools`);
                    poolsData = allPoolsHealth.map((poolHealth: any) => {
                        logger.log('Processing pool health data:', poolHealth);
                        return {
                            pool: poolHealth.pool,
                            token0: poolHealth.token0,
                            token1: poolHealth.token1,
                            token0Symbol: 'WETH', // Contract doesn't return symbols in getAllPoolsHealth
                            token1Symbol: 'MEMER',
                            isStable: poolHealth.isStable,
                            reserve0: poolHealth.reserve0.toString(),
                            reserve1: poolHealth.reserve1.toString(),
                            lpBalance: poolHealth.lpBalance.toString(),
                            lpTotalSupply: poolHealth.lpTotalSupply.toString(),
                            sharePercentage: poolHealth.sharePercentage.toNumber() / 100, // Convert from basis points
                            estimatedValue0: poolHealth.estimatedValue0.toString(),
                            estimatedValue1: poolHealth.estimatedValue1.toString(),
                            unclaimedFees0: poolHealth.unclaimedFees0.toString(),
                            unclaimedFees1: poolHealth.unclaimedFees1.toString(),
                            autoCompoundEnabled: false, // Will be updated below
                            lastCompoundTime: poolHealth.lastUpdated.toNumber(),
                            lastUpdated: Date.now()
                        };
                    });
                    setPools(poolsData);
                } else if (poolAddress) {
                    // Fallback to single pool if getAllPoolsHealth returns empty
                    logger.log('getAllPoolsHealth returned empty, trying single pool approach');
                    const poolHealth = await contract.getPoolHealth(poolAddress);
                    logger.log('Single pool health result:', poolHealth);
                    poolsData = [{
                        pool: poolAddress,
                        token0: poolHealth.token0,
                        token1: poolHealth.token1,
                        token0Symbol: 'WETH',
                        token1Symbol: 'MEMER',
                        isStable: poolHealth.isStable,
                        reserve0: poolHealth.reserve0.toString(),
                        reserve1: poolHealth.reserve1.toString(),
                        lpBalance: poolHealth.lpBalance.toString(),
                        lpTotalSupply: poolHealth.lpTotalSupply.toString(),
                        sharePercentage: poolHealth.sharePercentage.toNumber() / 100,
                        estimatedValue0: poolHealth.estimatedValue0.toString(),
                        estimatedValue1: poolHealth.estimatedValue1.toString(),
                        unclaimedFees0: poolHealth.unclaimedFees0.toString(),
                        unclaimedFees1: poolHealth.unclaimedFees1.toString(),
                        autoCompoundEnabled: false,
                        lastCompoundTime: poolHealth.lastUpdated.toNumber(),
                        lastUpdated: Date.now()
                    }];
                    setPools(poolsData);
                } else {
                    logger.log('No pool address available and getAllPoolsHealth returned empty - this is normal for new deployments');
                    setPools([]);
                }
            } catch (poolError) {
                logger.warn('Failed to get pool health data - this may be expected for new deployments:', poolError);
                setPools([]);
                // This is not necessarily an error - could be expected behavior
            }

            // Try to get auto-compound config for available pools
            if (poolsData.length > 0) {
                try {
                    // Get config for the first pool
                    const primaryPool = poolsData[0].pool;
                    const config = await contract.getAutoCompoundConfig(primaryPool);
                    setAutoCompoundConfig({
                        enabled: config.enabled,
                        minFeeThresholdETH: weiToEth(config.minFeeThresholdETH),
                        gasMultiplier: config.gasMultiplier.toString(),
                        minInterval: config.minCompoundInterval.toString(),
                        targetPool: config.targetPool,
                        lastCompoundTime: config.lastCompoundTime.toNumber()
                    });
                } catch (configError) {
                    logger.error('Failed to get auto-compound config:', configError);
                    setAutoCompoundConfig(null);
                }
            } else if (poolAddress) {
                // Fallback: try with the deployment pool address
                try {
                    const config = await contract.getAutoCompoundConfig(poolAddress);
                    setAutoCompoundConfig({
                        enabled: config.enabled,
                        minFeeThresholdETH: weiToEth(config.minFeeThresholdETH),
                        gasMultiplier: config.gasMultiplier.toString(),
                        minInterval: config.minCompoundInterval.toString(),
                        targetPool: config.targetPool,
                        lastCompoundTime: config.lastCompoundTime.toNumber()
                    });
                } catch (configError) {
                    logger.error('Failed to get auto-compound config for deployment pool:', configError);
                    setAutoCompoundConfig(null);
                }
            } else {
                setAutoCompoundConfig(null);
            }

            logger.log('Aerodrome fee management data refreshed successfully');
        } catch (err) {
            logger.error('Error refreshing fee management data:', err);
            setError(err instanceof Error ? err.message : 'Failed to refresh data');
        } finally {
            setLoading(false);
        }
    }, [contract, provider, getPoolAddress]);

    // Collect all fees
    const collectAllFees = useCallback(async (): Promise<string | null> => {
        if (!contract || !signer) {
            setError('Contract or signer not available');
            return null;
        }

        try {
            logger.log('Collecting all fees...');
            const tx = await contract.collectAllFees();
            logger.log('Collect all fees transaction sent:', tx.hash);
            return tx.hash;
        } catch (err) {
            logger.error('Error collecting all fees:', err);
            setError(err instanceof Error ? err.message : 'Failed to collect all fees');
            return null;
        }
    }, [contract, signer]);

    // Collect fees from specific pool
    const collectPoolFees = useCallback(async (poolAddress: string): Promise<string | null> => {
        if (!contract || !signer) {
            setError('Contract or signer not available');
            return null;
        }

        try {
            logger.log('Collecting pool fees...', { poolAddress });
            const tx = await contract.collectPoolFees(poolAddress);
            logger.log('Collect pool fees transaction sent:', tx.hash);
            return tx.hash;
        } catch (err) {
            logger.error('Error collecting pool fees:', err);
            setError(err instanceof Error ? err.message : 'Failed to collect pool fees');
            return null;
        }
    }, [contract, signer]);

    // Collect and reinvest fees
    const collectAndReinvest = useCallback(async (
        poolAddress: string,
        minToken0: string,
        minToken1: string
    ): Promise<string | null> => {
        if (!contract || !signer) {
            setError('Contract or signer not available');
            return null;
        }

        try {
            logger.log('Collecting and reinvesting fees...', { poolAddress, minToken0, minToken1 });
            const tx = await contract.collectAndReinvestFees(
                poolAddress,
                ethers.utils.parseEther(minToken0),
                ethers.utils.parseEther(minToken1)
            );
            logger.log('Collect and reinvest transaction sent:', tx.hash);
            return tx.hash;
        } catch (err) {
            logger.error('Error collecting and reinvesting fees:', err);
            setError(err instanceof Error ? err.message : 'Failed to collect and reinvest fees');
            return null;
        }
    }, [contract, signer]);

    // Enable auto-compound
    const enableAutoCompound = useCallback(async (
        poolAddress: string,
        threshold: string,
        gasMultiplier: string,
        interval: string
    ): Promise<string | null> => {
        if (!contract || !signer) {
            setError('Contract or signer not available');
            return null;
        }

        try {
            logger.log('Enabling auto-compound...', { poolAddress, threshold, gasMultiplier, interval });
            const tx = await contract.enableAutoCompounding(
                poolAddress,
                ethers.utils.parseEther(threshold),
                gasMultiplier,
                interval,
                poolAddress // targetPool same as source pool
            );
            logger.log('Enable auto-compound transaction sent:', tx.hash);
            return tx.hash;
        } catch (err) {
            logger.error('Error enabling auto-compound:', err);
            setError(err instanceof Error ? err.message : 'Failed to enable auto-compound');
            return null;
        }
    }, [contract, signer]);

    // Disable auto-compound
    const disableAutoCompound = useCallback(async (poolAddress: string): Promise<string | null> => {
        if (!contract || !signer) {
            setError('Contract or signer not available');
            return null;
        }

        try {
            logger.log('Disabling auto-compound...', { poolAddress });
            const tx = await contract.disableAutoCompounding(poolAddress);
            logger.log('Disable auto-compound transaction sent:', tx.hash);
            return tx.hash;
        } catch (err) {
            logger.error('Error disabling auto-compound:', err);
            setError(err instanceof Error ? err.message : 'Failed to disable auto-compound');
            return null;
        }
    }, [contract, signer]);

    // Auto-refresh data every 30 seconds
    useEffect(() => {
        if (contract) {
            refreshData();
            const interval = setInterval(refreshData, 30000);
            return () => clearInterval(interval);
        }
    }, [contract, refreshData]);

    return {
        feeMetrics,
        pools,
        autoCompoundConfig,
        loading,
        error,
        refreshData,
        collectAllFees,
        collectPoolFees,
        collectAndReinvest,
        enableAutoCompound,
        disableAutoCompound
    };
};
